import { useEffect, useState } from "react";
import { Card, DatePicker, Table, Button, Space, Typography, message, Row, Col, Spin, Skeleton, Select } from "antd";
import { useTranslation } from "react-i18next";
import { dashboardService } from "../../services/DashboardService";
import dayjs from "dayjs";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import type { Dayjs } from "dayjs";
import { useQuery } from 'react-query';
import { useAppSelector } from "../../store/Hooks";
import { StatCard } from "../../components/dashboard/StatCard";
import { PlusOutlined, SwapOutlined, CloseOutlined, ArrowRightOutlined } from '@ant-design/icons';
import AgencyCard from "../../components/dashboard/AgencyCard";
import SortedContainer, { ChartDataItem } from "../../components/dashboard/SortedContainer";

dayjs.extend(isSameOrBefore);

const { Title } = Typography;


const DashboardDetail = () => {
    const userHotels = useAppSelector(x => x.site.userHotels);
    const nonPaymentHotels = userHotels?.filter(x => x.requirePayment === false) || [];
    const crossAgencies = useAppSelector(x => x.site.crossAgencies);
    const { t } = useTranslation();
    const [dateRangeForReport, setDateRangeForReport] = useState<[Dayjs, Dayjs]>([dayjs().subtract(14, 'day'), dayjs()]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedHotels, setSelectedHotels] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(14, 'day'));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

    useEffect(() => {
        if (nonPaymentHotels.length > 0 && selectedHotels.length === 0) {
            setSelectedHotels([nonPaymentHotels[0].paximumId.toString()]);
        }
    }, [nonPaymentHotels]);

    const handleStartDateChange = (date: Dayjs | null) => {
        if (date) {
            setStartDate(date);
            setEndDate(null);
        }
    };

    const handleEndDateChange = (date: Dayjs | null) => {
        if (date) {
            setEndDate(date);
        }
    };

    const handleReport = () => {
        if (!startDate || !endDate) {
            message.warning(t('dashboard.selectDateRange'));
            return;
        }

        const [start, end] = startDate.isBefore(endDate)
            ? [startDate, endDate]
            : [endDate, startDate];

        setDateRangeForReport([start, end]);
    };

    const { data: dashboardData, isLoading } = useQuery(
        ["dashboard", dateRangeForReport, selectedHotels],
        () => dashboardService.getStats({
            startDate: dateRangeForReport[0].format('YYYY-MM-DD'),
            endDate: dateRangeForReport[1].format('YYYY-MM-DD'),
            days: 15,
            includeNights: true,
            agencyIds: (crossAgencies?.map(x => x.id) || []) as string[],
            paximumIds: selectedHotels.map(id => Number(id)),
        }),
        {
            refetchOnWindowFocus: false,
            enabled: selectedHotels.length > 0
        }
    );

    const fillMissingDates = (data: ChartDataItem[], startDate: Dayjs, endDate: Dayjs) => {
        const filledData: ChartDataItem[] = [];
        let currentDate = startDate;

        while (currentDate.isSameOrBefore(endDate)) {
            const dateStr = currentDate.format('DD.MM.YYYY');
            const existingData = data.find(item => item.date === dateStr);

            filledData.push({
                date: dateStr,
                new: existingData?.new || 0,
                newNights: existingData?.newNights || 0,
                changed: existingData?.changed || 0,
                changedNights: existingData?.changedNights || 0,
                cancelled: existingData?.cancelled || 0,
                cancelledNights: existingData?.cancelledNights || 0
            });

            currentDate = currentDate.add(1, 'day');
        }

        return filledData;
    };


    const filledChartData = dashboardData ? fillMissingDates(dashboardData.chartData, dateRangeForReport[0], dateRangeForReport[1]) : [];
    const sortedData = [...filledChartData].sort((a, b) =>
        dayjs(a.date, 'DD.MM.YYYY').unix() - dayjs(b.date, 'DD.MM.YYYY').unix()
    );

    if (!dashboardData) {
        return (
            <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
                <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
                    <Row gutter={[24, 24]}>
                        <Col span={24}>
                            <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                                <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between' }}>
                                    <Title level={4}>{t('dashboard.detailedReport')}</Title>
                                    <Space>
                                        <Select
                                            mode="multiple"
                                            style={{ minWidth: '200px' }}
                                            placeholder={t('common.selectHotels')}
                                            value={selectedHotels}
                                            onChange={setSelectedHotels}
                                            options={nonPaymentHotels.map(hotel => ({
                                                label: hotel.localName,
                                                value: hotel.paximumId.toString()
                                            }))}
                                        />
                                        <Space.Compact>
                                            <DatePicker
                                                value={startDate}
                                                onChange={handleStartDateChange}
                                                disabledDate={(current) => {
                                                    if (!current) return false;
                                                    return current > dayjs().endOf('day');
                                                }}
                                                format="DD.MM.YYYY"
                                                placeholder={t('dashboard.startDate')}
                                                status={!startDate && endDate ? "error" : undefined}
                                            />
                                            <DatePicker
                                                value={endDate}
                                                onChange={handleEndDateChange}
                                                disabledDate={(current) => {
                                                    if (!current || !startDate) return false;
                                                    if (current > dayjs().endOf('day')) return true;
                                                    return Math.abs(startDate.diff(current, 'days')) > 14;
                                                }}
                                                format="DD.MM.YYYY"
                                                placeholder={t('dashboard.endDate')}
                                                status={startDate && !endDate ? "error" : undefined}
                                            />
                                        </Space.Compact>
                                        <Button
                                            type="primary"
                                            onClick={handleReport}
                                        >
                                            {t('common.report')}
                                        </Button>
                                    </Space>
                                </Space>
                            </Card>
                        </Col>

                        {[1, 2, 3, 4].map((_, index) => (
                            <Col xs={24} sm={12} md={6} key={index}>
                                <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                                    <Skeleton active paragraph={{ rows: 1 }} />
                                </Card>
                            </Col>
                        ))}

                        <Col span={24}>
                            <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                                <Skeleton active paragraph={{ rows: 8 }} />
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                                <Skeleton active paragraph={{ rows: 10 }} />
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                            <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Title level={4}>{t('dashboard.detailedReport')}</Title>
                                <Space>
                                    <Select
                                        mode="multiple"
                                        style={{ minWidth: '200px' }}
                                        placeholder={t('common.selectHotels')}
                                        value={selectedHotels}
                                        onChange={setSelectedHotels}
                                        options={nonPaymentHotels.map(hotel => ({
                                            label: hotel.localName,
                                            value: hotel.paximumId.toString()
                                        }))}
                                    />
                                    <Space.Compact>
                                        <DatePicker
                                            value={startDate}
                                            onChange={handleStartDateChange}
                                            disabledDate={(current) => {
                                                if (!current) return false;
                                                return current > dayjs().endOf('day');
                                            }}
                                            format="DD.MM.YYYY"
                                            placeholder={t('dashboard.startDate')}
                                            status={!startDate && endDate ? "error" : undefined}
                                        />
                                        <DatePicker
                                            value={endDate}
                                            onChange={handleEndDateChange}
                                            disabledDate={(current) => {
                                                if (!current || !startDate) return false;
                                                if (current > dayjs().endOf('day')) return true;
                                                return Math.abs(startDate.diff(current, 'days')) > 14;
                                            }}
                                            format="DD.MM.YYYY"
                                            placeholder={t('dashboard.endDate')}
                                            status={startDate && !endDate ? "error" : undefined}
                                        />
                                    </Space.Compact>
                                    <Button
                                        type="primary"
                                        onClick={handleReport}
                                    >
                                        {t('common.report')}
                                    </Button>
                                </Space>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Card loading={isLoading}>
                            <StatCard
                                title={`${t('dashboard.new')} ${t('dashboard.reservations').toLowerCase()}`}
                                count={dashboardData.stats.new.count}
                                nights={dashboardData.stats.new.nights}
                                color="#52c41a"
                                icon={<PlusOutlined style={{ fontSize: '24px', color: '#52c41a' }} />}
                                loading={isLoading}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card loading={isLoading}>
                            <StatCard
                                title={`${t('dashboard.changed')} ${t('dashboard.reservations').toLowerCase()}`}
                                count={dashboardData.stats.changed.count}
                                nights={dashboardData.stats.changed.nights}
                                color="#faad14"
                                icon={<SwapOutlined style={{ fontSize: '24px', color: '#faad14' }} />}
                                loading={isLoading}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card loading={isLoading}>
                            <StatCard
                                title={`${t('dashboard.cancelled')} ${t('dashboard.reservations').toLowerCase()}`}
                                count={dashboardData.stats.cancelled.count}
                                nights={dashboardData.stats.cancelled.nights}
                                color="#ff4d4f"
                                icon={<CloseOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />}
                                loading={isLoading}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card loading={isLoading}>
                            <StatCard
                                title={`${t('dashboard.total')} ${t('dashboard.reservations').toLowerCase()}`}
                                count={dashboardData.stats.total.count}
                                nights={dashboardData.stats.total.nights}
                                color="#722ed1"
                                icon={<ArrowRightOutlined style={{ fontSize: '24px', color: '#722ed1' }} />}
                                loading={isLoading}
                            />
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Title level={4} style={{ margin: 0 }}>{t('dashboard.reservationTrend')}</Title>
                                <div style={{ height: '400px', position: 'relative' }}>
                                    {isLoading && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'rgba(255, 255, 255, 0.7)',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            zIndex: 1
                                        }}>
                                            <Spin size="large" />
                                        </div>
                                    )}
                                    <SortedContainer sortedData={sortedData} />
                                </div>
                            </Space>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card loading={isLoading} bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Title level={4} style={{ margin: 0 }}>{t('dashboard.agencyPerformance')}</Title>
                                <Row gutter={[24, 24]}>
                                    {dashboardData.agencies.map((agency, index) => (
                                        <AgencyCard agency={agency} index={index} />
                                    ))}
                                </Row>
                            </Space>
                        </Card>
                    </Col>

                    <Col span={24}>
                        <Card bordered={false} bodyStyle={{ padding: '24px' }} style={{ borderRadius: '16px' }}>
                            <Table
                                columns={[
                                    {
                                        title: t('dashboard.date'),
                                        dataIndex: 'date',
                                        key: 'date',
                                        sorter: (a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix()
                                    },
                                    {
                                        title: t('dashboard.new'),
                                        children: [
                                            {
                                                title: t('dashboard.reservations'),
                                                dataIndex: 'new',
                                                key: 'new',
                                                sorter: (a: any, b: any) => a.new - b.new
                                            },
                                            {
                                                title: t('dashboard.nights'),
                                                dataIndex: 'newNights',
                                                key: 'newNights',
                                                sorter: (a: any, b: any) => a.newNights - b.newNights,
                                                render: (value: number) => value?.toLocaleString() || '-'
                                            }
                                        ]
                                    },
                                    {
                                        title: t('dashboard.changed'),
                                        children: [
                                            {
                                                title: t('dashboard.reservations'),
                                                dataIndex: 'changed',
                                                key: 'changed',
                                                sorter: (a: any, b: any) => a.changed - b.changed
                                            },
                                            {
                                                title: t('dashboard.nights'),
                                                dataIndex: 'changedNights',
                                                key: 'changedNights',
                                                sorter: (a: any, b: any) => a.changedNights - b.changedNights,
                                                render: (value: number) => value?.toLocaleString() || '-'
                                            }
                                        ]
                                    },
                                    {
                                        title: t('dashboard.cancelled'),
                                        children: [
                                            {
                                                title: t('dashboard.reservations'),
                                                dataIndex: 'cancelled',
                                                key: 'cancelled',
                                                sorter: (a: any, b: any) => a.cancelled - b.cancelled
                                            },
                                            {
                                                title: t('dashboard.nights'),
                                                dataIndex: 'cancelledNights',
                                                key: 'cancelledNights',
                                                sorter: (a: any, b: any) => a.cancelledNights - b.cancelledNights,
                                                render: (value: number) => value?.toLocaleString() || '-'
                                            }
                                        ]
                                    }
                                ]}
                                dataSource={sortedData}
                                loading={isLoading}
                                rowKey="date"
                                pagination={{
                                    current: currentPage,
                                    onChange: (page) => setCurrentPage(page),
                                    total: sortedData?.length,
                                    pageSize: 15,
                                    showTotal: (total) => t('common.totalItems', { total })
                                }}
                                bordered
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default DashboardDetail; 